import type { FieldType, SigningField } from '../types/fields';

export type StandardFontFamilyKey = 'sans' | 'serif' | 'mono';
/** Cursive signature families — rendered server-side with bundled TTFs
 *  (server/fonts), previewed client-side via Google Fonts CSS stacks. */
export type SignatureFontFamilyKey =
  | 'signature-greatvibes'
  | 'signature-dancing'
  | 'signature-caveat'
  | 'signature-sacramento';
export type FontFamilyKey = StandardFontFamilyKey | SignatureFontFamilyKey;
export type TextAlignKey = 'left' | 'center' | 'right';

export function isSignatureFontFamily(
  family: FontFamilyKey | string | undefined
): family is SignatureFontFamilyKey {
  return (
    family === 'signature-greatvibes' ||
    family === 'signature-dancing' ||
    family === 'signature-caveat' ||
    family === 'signature-sacramento'
  );
}

/** pdf-lib StandardFonts enum member names */
export type StandardFontName =
  | 'Helvetica'
  | 'HelveticaBold'
  | 'HelveticaOblique'
  | 'HelveticaBoldOblique'
  | 'TimesRoman'
  | 'TimesRomanBold'
  | 'TimesRomanItalic'
  | 'TimesRomanBoldItalic'
  | 'Courier'
  | 'CourierBold'
  | 'CourierOblique'
  | 'CourierBoldOblique';

export interface ResolvedTextStyle {
  fontFamily: FontFamilyKey;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  align: TextAlignKey;
  color: string;
  highlightColor: string | null;
  lineHeight: number;
  letterSpacing: number;
  fontSize: number;
  standardFontName: StandardFontName;
  metricsFamily: string;
}

export const DEFAULT_TEXT_COLOR = '#0d0d1a';
export const FONT_SIZE_PRESETS = [8, 10, 12, 14, 18, 24, 36] as const;

export function isTextBasedFieldType(type: FieldType): boolean {
  return ['text', 'name', 'date', 'signature-type', 'initials', 'text-bubble'].includes(type);
}

/** Per-type defaults when style props are unset (backward compatible). */
export function defaultStyleForFieldType(type: FieldType): Pick<
  ResolvedTextStyle,
  'fontFamily' | 'bold' | 'italic'
> {
  if (type === 'signature-type') {
    return { fontFamily: 'signature-greatvibes', bold: false, italic: false };
  }
  if (type === 'name') {
    return { fontFamily: 'sans', bold: true, italic: false };
  }
  return { fontFamily: 'sans', bold: false, italic: false };
}

export function resolveStandardFontName(
  family: FontFamilyKey,
  bold: boolean,
  italic: boolean
): StandardFontName {
  if (isSignatureFontFamily(family)) {
    // Standard-font FALLBACK only (server uses it if the bundled TTF fails).
    return bold ? 'TimesRomanBoldItalic' : 'TimesRomanItalic';
  }
  if (family === 'serif') {
    if (bold && italic) return 'TimesRomanBoldItalic';
    if (bold) return 'TimesRomanBold';
    if (italic) return 'TimesRomanItalic';
    return 'TimesRoman';
  }
  if (family === 'mono') {
    if (bold && italic) return 'CourierBoldOblique';
    if (bold) return 'CourierBold';
    if (italic) return 'CourierOblique';
    return 'Courier';
  }
  if (bold && italic) return 'HelveticaBoldOblique';
  if (bold) return 'HelveticaBold';
  if (italic) return 'HelveticaOblique';
  return 'Helvetica';
}

export function metricsFamilyFromStandardName(name: StandardFontName): string {
  const lower = name.toLowerCase();
  if (lower.includes('times')) return 'times';
  if (lower.includes('courier')) return 'courier';
  return 'helvetica';
}

export function cssFontStack(family: FontFamilyKey): string {
  // Google Fonts families (stylesheet linked in index.html) for cursive previews.
  if (family === 'signature-greatvibes') return "'Great Vibes', cursive";
  if (family === 'signature-dancing') return "'Dancing Script', cursive";
  if (family === 'signature-caveat') return "'Caveat', cursive";
  if (family === 'signature-sacramento') return "'Sacramento', cursive";
  if (family === 'serif') return "'Times New Roman', Times, serif";
  if (family === 'mono') return "'Courier New', Courier, monospace";
  return 'Helvetica, Arial, sans-serif';
}

export function normalizeHexColor(input: string | undefined, fallback: string): string {
  if (!input || typeof input !== 'string') return fallback;
  const t = input.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = normalizeHexColor(hex, DEFAULT_TEXT_COLOR).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

/** layoutMode mirrors fieldTextLayoutMode — unset align follows legacy per-mode alignment. */
export function resolveFieldTextStyle(
  field: SigningField,
  layoutMode?: 'centered' | 'form-line'
): ResolvedTextStyle {
  const defaults = defaultStyleForFieldType(field.type);
  const fontFamily = field.fontFamily ?? defaults.fontFamily;
  const bold = field.bold ?? defaults.bold;
  const italic = field.italic ?? defaults.italic;
  const standardFontName = resolveStandardFontName(fontFamily, bold, italic);
  const align =
    field.align ?? (layoutMode === 'centered' ? 'center' : 'left');

  return {
    fontFamily,
    bold,
    italic,
    underline: !!field.underline,
    strikethrough: !!field.strikethrough,
    align,
    color: normalizeHexColor(field.color, DEFAULT_TEXT_COLOR),
    highlightColor: field.highlightColor
      ? normalizeHexColor(field.highlightColor, DEFAULT_TEXT_COLOR)
      : null,
    lineHeight: field.lineHeight ?? 1.2,
    letterSpacing: field.letterSpacing ?? 0,
    fontSize: field.fontSize ?? (field.type.includes('signature') ? 16 : 13),
    standardFontName,
    // 'script' is unknown to approximateFontHeight, so it uses the 0.925
    // default — the same approximation the server applies to embedded TTFs,
    // keeping preview and output baselines aligned for cursive fonts.
    metricsFamily: isSignatureFontFamily(fontFamily)
      ? 'script'
      : metricsFamilyFromStandardName(standardFontName),
  };
}

export interface PreviewTextCss {
  fontFamily: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  textDecoration: string;
  color: string;
  backgroundColor: string | undefined;
  textAlign: TextAlignKey;
  lineHeight: number;
  letterSpacing: string;
  fontSize: string;
}

export function toPreviewCss(style: ResolvedTextStyle, displayScale = 1): PreviewTextCss {
  const decorations: string[] = [];
  if (style.underline) decorations.push('underline');
  if (style.strikethrough) decorations.push('line-through');

  return {
    fontFamily: cssFontStack(style.fontFamily),
    fontWeight: style.bold ? 700 : 400,
    fontStyle: style.italic ? 'italic' : 'normal',
    textDecoration: decorations.length ? decorations.join(' ') : 'none',
    color: style.color,
    backgroundColor: style.highlightColor ?? undefined,
    textAlign: style.align,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing ? `${style.letterSpacing * displayScale}px` : 'normal',
    fontSize: `${Math.round(style.fontSize * displayScale)}px`,
  };
}

// FUTURE: per-character rich text would require per-run font selection + segmented drawText
// calls (or @pdf-lib/fontkit + embedded TTF for Unicode). v1 = whole-field styling only.